import React from 'react';
import { Gleam, Tabs, Text } from '@zillow/constellation';
import { ITasksParams, useListPagedTasksByFilterParamsQuery } from '../../../../API/pcDashboard/getTasksAsync';
import { IPCDashboardTaskData } from '../../../../types/pcDashboard/IDashboardTasks';
import { createDefaultPageData } from '../../../../types/rest/IPageData';
import { useGetImportantDays } from '../hooks/useGetImportantDays';

export enum TasksTabNames {
  AllTasks = 'all-tasks-tab',
  DueTodayTasks = 'due-today-tasks-tab',
  DueTomorrowTasks = 'due-tomorrow-tasks-tab',
  DueThisWeekTasks = 'due-this-week-tasks-tab',
  PastDueTasks = 'past-due-tasks-tab',
}

export const TaskTabList: React.FC<{
  selectedTab: string;
  setSelectedTab: React.Dispatch<React.SetStateAction<TasksTabNames>>;
  taskParams: ITasksParams;
  revertToFirstPage: () => void;
}> = ({ selectedTab, setSelectedTab, taskParams, revertToFirstPage }) => {
  const { todayCount, tomorrowCount, weekCount, pastDueCount } = useFetchCountsForTabTitleGleams(taskParams);

  return (
    <Tabs selected={selectedTab} onSelected={setSelectedTab}>
      <Tabs.List aria-label="File tabs example">
        <Tabs.Tab id={TasksTabNames.AllTasks} onClick={revertToFirstPage}>
          All
        </Tabs.Tab>
        <Tabs.Tab id={TasksTabNames.DueTodayTasks} onClick={revertToFirstPage}>
          <TitleAndGleam count={todayCount}>Due Today</TitleAndGleam>
        </Tabs.Tab>
        <Tabs.Tab id={TasksTabNames.DueTomorrowTasks} onClick={revertToFirstPage}>
          <TitleAndGleam count={tomorrowCount}>Due Tomorrow</TitleAndGleam>
        </Tabs.Tab>
        <Tabs.Tab id={TasksTabNames.DueThisWeekTasks} onClick={revertToFirstPage}>
          <TitleAndGleam count={weekCount}>Due This Week</TitleAndGleam>
        </Tabs.Tab>
        <Tabs.Tab id={TasksTabNames.PastDueTasks} onClick={revertToFirstPage}>
          <TitleAndGleam count={pastDueCount} isPastDue>
            Past Due
          </TitleAndGleam>
        </Tabs.Tab>
      </Tabs.List>
    </Tabs>
  );
};

const getAppearance = (isPastDue?: boolean) => {
  if (!isPastDue) {
    return 'info';
  }
  return 'default';
};

export const TitleAndGleam: React.FC<{ children: React.ReactNode; count: number; isPastDue?: boolean }> = ({
  children,
  count,
  isPastDue,
}) => {
  const appearance = getAppearance(isPastDue);
  return (
    <Text>
      {children} <Gleam appearance={appearance} count={count} maxCount={99} maxSuffix />
    </Text>
  );
};

const useFetchCountsForTabTitleGleams = (taskParams: ITasksParams) => {
  const { today, tomorrow, dayAfterTomorrow, endWeek, startWeek } = useGetImportantDays();

  const { data: todayData = createDefaultPageData<IPCDashboardTaskData>() } = useListPagedTasksByFilterParamsQuery({
    ...taskParams,
    startDate: today,
    endDate: tomorrow,
    page: 0,
    size: 1,
  });

  const { data: tomorrowData = createDefaultPageData<IPCDashboardTaskData>() } = useListPagedTasksByFilterParamsQuery({
    ...taskParams,
    startDate: tomorrow,
    endDate: dayAfterTomorrow,
    page: 0,
    size: 1,
  });

  const { data: thisWeekData = createDefaultPageData<IPCDashboardTaskData>() } = useListPagedTasksByFilterParamsQuery({
    ...taskParams,
    startDate: startWeek,
    endDate: endWeek,
    page: 0,
    size: 1,
  });

  const { data: pastDueData = createDefaultPageData<IPCDashboardTaskData>() } = useListPagedTasksByFilterParamsQuery({
    ...taskParams,
    startDate: undefined,
    endDate: today,
    page: 0,
    size: 1,
  });
  return {
    todayCount: todayData.totalElements,
    tomorrowCount: tomorrowData.totalElements,
    weekCount: thisWeekData.totalElements,
    pastDueCount: pastDueData.totalElements,
  };
};
